import React from 'react';
import {useHistory} from 'react-router-dom';

import {Button} from '../../../../../components/Button';
import {Tailwind} from '../../../../../components/Tailwind';
import {baseContext, PEEK_PARAM, useEntityProject} from '../context';
import {useCallsSummaryContext} from '../pages/CallsPage/CallsSummaryContext';
import {callsChartsStyles} from './CallsCharts.styles';
import {getOpNameDisplay} from './extractData';
import {COLOR_PALETTE, formatTooltipDate, formatTooltipValue} from './format';
import {SparklineCard, SparklineCardRow} from './SparklineCard';
import {ExtractedCallData} from './types';

export const EvalsSummary: React.FC<{
  data?: ExtractedCallData[];
  visibleCallIds?: string[];
}> = ({data, visibleCallIds}) => {
  const history = useHistory();
  const {entity, project} = useEntityProject();
  const ctx = useCallsSummaryContext();
  const [isCollapsed, setIsCollapsed] = React.useState(false);

  // Build aligned series from provided data
  const sortedCalls = React.useMemo(() => {
    const effectiveDataLocal = data ?? ctx?.data ?? [];
    const effectiveVisibleIdsLocal = visibleCallIds ?? ctx?.visibleCallIds;
    if (!effectiveDataLocal) return [] as ExtractedCallData[];
    const filtered =
      effectiveVisibleIdsLocal && effectiveVisibleIdsLocal.length > 0
        ? effectiveDataLocal.filter(d =>
            effectiveVisibleIdsLocal.includes(d.callId)
          )
        : effectiveDataLocal;
    // Only include finished calls (those with an ended_at timestamp)
    const finishedOnly = filtered.filter(c => !!c.ended_at);
    return [...finishedOnly].sort(
      (a, b) =>
        new Date(a.started_at).getTime() - new Date(b.started_at).getTime()
    );
  }, [data, visibleCallIds, ctx]);

  const costData: Array<number | null> = React.useMemo(
    () => sortedCalls.map(c => (typeof c.cost === 'number' ? c.cost! : null)),
    [sortedCalls]
  );
  const tokenData: Array<number | null> = React.useMemo(
    () =>
      sortedCalls.map(c => {
        const t = (c.prompt_tokens || 0) + (c.completion_tokens || 0);
        return t === 0 ? null : t;
      }),
    [sortedCalls]
  );
  const latencyData: Array<number | null> = React.useMemo(
    () =>
      sortedCalls.map(c => (typeof c.latency === 'number' ? c.latency! : null)),
    [sortedCalls]
  );

  // Helper to filter series arrays by a boolean mask
  const filterSeriesByMask = React.useCallback(
    (series: Array<number | null>, mask: boolean[]) =>
      series.filter((_, idx) => mask[idx]),
    []
  );

  // For the General card, drop x-points where all series are null
  const generalKeepMask: boolean[] = React.useMemo(() => {
    return sortedCalls.map((_, idx) => {
      const hasCost = costData[idx] != null;
      const hasTokens = tokenData[idx] != null;
      const hasLatency = latencyData[idx] != null;
      return hasCost || hasTokens || hasLatency;
    });
  }, [sortedCalls, costData, tokenData, latencyData]);

  const generalSortedCalls: ExtractedCallData[] = React.useMemo(
    () => sortedCalls.filter((_, idx) => generalKeepMask[idx]),
    [sortedCalls, generalKeepMask]
  );

  const filteredCostData = React.useMemo(
    () => filterSeriesByMask(costData, generalKeepMask),
    [costData, generalKeepMask, filterSeriesByMask]
  );
  const filteredTokenData = React.useMemo(
    () => filterSeriesByMask(tokenData, generalKeepMask),
    [tokenData, generalKeepMask, filterSeriesByMask]
  );
  const filteredLatencyData = React.useMemo(
    () => filterSeriesByMask(latencyData, generalKeepMask),
    [latencyData, generalKeepMask, filterSeriesByMask]
  );

  // Build per-namespace (output.<name>) metric series across calls
  const outputsByNamespace: {
    [namespace: string]: {[subKey: string]: Array<number | null>};
  } = React.useMemo(() => {
    if (!sortedCalls || sortedCalls.length === 0) {
      return {};
    }
    const result: {[ns: string]: {[subKey: string]: Array<number | null>}} = {};

    const isNumber = (v: unknown): v is number =>
      typeof v === 'number' && !isNaN(v);
    const isBoolean = (v: unknown): v is boolean => typeof v === 'boolean';

    const flatten = (
      obj: unknown,
      collector: {[key: string]: number},
      prefix = ''
    ) => {
      if (obj == null) return;
      if (Array.isArray(obj)) {
        // skip arrays in summary for now
        return;
      }
      if (typeof obj !== 'object') {
        if (isNumber(obj)) {
          const key = prefix || 'value';
          collector[key] = Number(obj);
        } else if (isBoolean(obj)) {
          const key = prefix || 'value';
          collector[key] = obj ? 1 : 0;
        }
        return;
      }
      Object.entries(obj as Record<string, unknown>).forEach(([k, v]) => {
        if (k.startsWith('_')) return;
        const path = prefix ? `${prefix}.${k}` : k;
        if (isNumber(v)) {
          collector[path] = Number(v);
        } else if (isBoolean(v)) {
          collector[path] = v ? 1 : 0;
        } else if (v && typeof v === 'object' && !Array.isArray(v)) {
          flatten(v, collector, path);
        }
      });
    };

    sortedCalls.forEach((_call, idx) => {
      const out = _call.output;
      if (!out || typeof out !== 'object' || Array.isArray(out)) return;
      Object.entries(out).forEach(([ns, val]) => {
        if (!result[ns]) result[ns] = {};
        const collector: {[key: string]: number} = {};
        flatten(val, collector, '');
        Object.entries(collector).forEach(([subKey, v]) => {
          if (!result[ns][subKey]) {
            result[ns][subKey] = new Array(sortedCalls.length).fill(null);
          }
          result[ns][subKey][idx] = v;
        });
      });
    });
    return result;
  }, [sortedCalls]);

  const formatValueShort = (value: number, unit?: string) => {
    // Delegate to shared formatter to ensure consistency with tooltip values
    return formatTooltipValue(value, unit);
  };

  const mainRows: SparklineCardRow[] = React.useMemo(
    () => [
      {
        id: 'cost',
        label: 'Cost',
        icon: 'cost',
        color: COLOR_PALETTE[0],
        series: filteredCostData,
        unit: 'USD',
      },
      {
        id: 'tokens',
        label: 'Tokens',
        icon: 'type-number-alt',
        color: COLOR_PALETTE[1],
        series: filteredTokenData,
      },
      {
        id: 'latency',
        label: 'Latency',
        icon: 'recent-clock',
        color: COLOR_PALETTE[2],
        series: filteredLatencyData,
        unit: 's',
      },
    ],
    [filteredCostData, filteredTokenData, filteredLatencyData]
  );

  // Build filtered namespace data (drop x-points with no values across sub-keys)
  const filteredNamespaces = React.useMemo(() => {
    const result: {
      [namespace: string]: {
        seriesMap: {[subKey: string]: Array<number | null>};
        subKeys: string[];
        calls: ExtractedCallData[];
      };
    } = {};
    Object.keys(outputsByNamespace).forEach(namespace => {
      const seriesMap = outputsByNamespace[namespace];
      const subKeys = Object.keys(seriesMap);
      if (subKeys.length === 0) return;
      const mask = sortedCalls.map((_, idx) =>
        subKeys.some(sk => seriesMap[sk][idx] != null)
      );
      const anyKept = mask.some(Boolean);
      if (!anyKept) return;
      const filteredSeriesMap: {[subKey: string]: Array<number | null>} = {};
      subKeys.forEach(sk => {
        filteredSeriesMap[sk] = filterSeriesByMask(seriesMap[sk], mask);
      });
      const calls = sortedCalls.filter((_, idx) => mask[idx]);
      result[namespace] = {seriesMap: filteredSeriesMap, subKeys, calls};
    });
    return result;
  }, [outputsByNamespace, sortedCalls, filterSeriesByMask]);

  const totalCardCount = React.useMemo(() => {
    return Object.keys(filteredNamespaces).length;
  }, [filteredNamespaces]);

  const createSparklineClick = React.useCallback(
    (callsForCard: ExtractedCallData[]) => (hoverIdx: number) => {
      const call = callsForCard[hoverIdx];
      if (!call || !entity || !project) return;
      const callPeekComponent = baseContext.callUIUrl(
        entity,
        project,
        call.traceId,
        call.callId,
        undefined
      );
      const currentLocation = history.location;
      const searchParams = new URLSearchParams(currentLocation.search);
      searchParams.set(PEEK_PARAM, callPeekComponent);
      history.push({
        pathname: currentLocation.pathname,
        search: searchParams.toString(),
      });
    },
    [entity, project, history]
  );

  return (
    <Tailwind>
      <div style={{padding: 0}}>
        <div className={callsChartsStyles.header}>
          <div className={callsChartsStyles.headerContent}>
            <div className={callsChartsStyles.headerLeft}>
              <Button
                variant="ghost"
                size="small"
                icon={isCollapsed ? 'chevron-next' : 'chevron-down'}
                tooltip={isCollapsed ? 'Expand summary' : 'Collapse summary'}
                onClick={() => setIsCollapsed(prev => !prev)}
              />
              <div className={callsChartsStyles.headerText}>Score summary</div>
              <div className={callsChartsStyles.headerCount}>
                {totalCardCount}
              </div>
            </div>
          </div>
        </div>

        {!isCollapsed && (
          <div className={`${callsChartsStyles.chartsContainer} gap-12 p-0`}>
            <SparklineCard
              header="General"
              rows={mainRows}
              formatCurrentValue={formatValueShort}
              formatTooltipValueFn={formatTooltipValue}
              tooltipMaxWidth={360}
              tooltipMinWidth={320}
              collapsible={true}
              defaultCollapsed={false}
              onSparklineClick={createSparklineClick(generalSortedCalls)}
              tooltipHeaderRenderer={(hoverIdx: number) => {
                const call = generalSortedCalls[hoverIdx];
                if (!call) return null;
                const when = formatTooltipDate(
                  new Date(call.started_at).getTime()
                );
                const name =
                  call.display_name ||
                  getOpNameDisplay(call.op_name) ||
                  call.op_name ||
                  '';
                return (
                  <div>
                    <div
                      className="text-moon-950 text-xs"
                      style={{fontWeight: 600}}>
                      {when}
                    </div>
                    {name ? (
                      <div
                        className="text-moon-700 text-xs"
                        style={{marginTop: 2}}>
                        {name}
                      </div>
                    ) : null}
                  </div>
                );
              }}
            />

            {Object.entries(filteredNamespaces).map(
              ([namespace, {seriesMap, subKeys, calls}]) => {
                if (subKeys.length === 0) return null;
                return (
                  <NamespaceCard
                    key={namespace}
                    namespace={namespace}
                    seriesMap={seriesMap}
                    subKeys={subKeys}
                    calls={calls}
                  />
                );
              }
            )}
          </div>
        )}
      </div>
    </Tailwind>
  );
};

/**
 * Card component for a single output namespace using the shared SparklineCard.
 */
const NamespaceCard: React.FC<{
  namespace: string;
  seriesMap: {[subKey: string]: Array<number | null>};
  subKeys: string[];
  calls: ExtractedCallData[];
}> = ({namespace, seriesMap, subKeys, calls}) => {
  const history = useHistory();
  const {entity, project} = useEntityProject();
  const rows: SparklineCardRow[] = React.useMemo(
    () =>
      subKeys.map((subKey, idx) => ({
        id: subKey,
        label: subKey,
        color: COLOR_PALETTE[idx % COLOR_PALETTE.length],
        series: seriesMap[subKey],
      })),
    [seriesMap, subKeys]
  );

  return (
    <SparklineCard
      header={namespace}
      rows={rows}
      formatCurrentValue={(value: number) => formatTooltipValue(value)}
      formatTooltipValueFn={(value: number | null) => formatTooltipValue(value)}
      onSparklineClick={(hoverIdx: number) => {
        const call = calls[hoverIdx];
        if (!call || !entity || !project) return;
        const callPeekComponent = baseContext.callUIUrl(
          entity,
          project,
          call.traceId,
          call.callId,
          undefined
        );
        const currentLocation = history.location;
        const searchParams = new URLSearchParams(currentLocation.search);
        searchParams.set(PEEK_PARAM, callPeekComponent);
        history.push({
          pathname: currentLocation.pathname,
          search: searchParams.toString(),
        });
      }}
      tooltipHeaderRenderer={(hoverIdx: number) => {
        const call = calls[hoverIdx];
        if (!call) return null;
        const when = formatTooltipDate(new Date(call.started_at).getTime());
        const name =
          call.display_name ||
          getOpNameDisplay(call.op_name) ||
          call.op_name ||
          '';
        return (
          <div>
            <div className="text-moon-950 text-xs" style={{fontWeight: 600}}>
              {when}
            </div>
            {name ? (
              <div className="text-moon-700 text-xs" style={{marginTop: 2}}>
                {name}
              </div>
            ) : null}
          </div>
        );
      }}
      tooltipMaxWidth={360}
      tooltipMinWidth={320}
      collapsible={true}
      defaultCollapsed={false}
    />
  );
};

export default EvalsSummary;
